'use client';

import React, { useState, useCallback, memo, useMemo, useEffect } from 'react';

import * as Popover from '@radix-ui/react-popover';

import { cn } from '@/lib/utils/cn';

export interface I_HoverPopoverProps {
  /** The trigger element that will show the popover on hover */
  trigger: React.ReactNode;

  /** The content to display inside the popover */
  children: React.ReactNode;

  /** Additional CSS classes for the popover content */
  className?: string;

  /** Position of the popover relative to trigger */
  side?: 'top' | 'right' | 'bottom' | 'left';

  /** Alignment of the popover */
  align?: 'start' | 'center' | 'end';

  /** Offset from the trigger element */
  sideOffset?: number;

  /** Offset for alignment */
  alignOffset?: number;

  /** Whether to show an arrow pointing to the trigger */
  showArrow?: boolean;

  /** Custom arrow styling */
  arrowClassName?: string;

  /** Whether the popover is disabled */
  disabled?: boolean;

  /** Accessible label for the trigger */
  triggerAriaLabel?: string;
}

export const HoverPopover = memo(function HoverPopover({
  trigger,
  children,
  className,
  side = 'bottom',
  align = 'center',
  sideOffset = 10,
  alignOffset = 0,
  showArrow = true,
  arrowClassName,
  disabled = false,
  triggerAriaLabel
}: I_HoverPopoverProps) {
  const [open, setOpen] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // Detect if it's a touch device
  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (disabled) return;
    setOpen(true);
  }, [disabled]);

  const handleMouseLeave = useCallback(() => {
    // Only close on mouse leave if it's not a touch device
    if (!isTouchDevice) {
      setOpen(false);
    }
  }, [isTouchDevice]);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (disabled) return;
      e.preventDefault();
      setOpen(true);
    },
    [disabled]
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      e.preventDefault();
      setOpen(true);
    },
    [disabled]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    },
    [disabled]
  );

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (disabled) return;
      setOpen(newOpen);
    },
    [disabled]
  );

  // Memoize className computation
  const contentClassName = useMemo(
    () =>
      cn(
        'rounded-md outline-none ring-1 bg-popover relative max-w-96 px-3 py-2.5',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
        'data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
        'origin-[var(--radix-popover-content-transform-origin)]',
        'z-40 min-w-36 max-w-65',
        className
      ),
    [className]
  );

  const arrowClassNameMemo = useMemo(
    () =>
      cn('fill-popover', 'drop-shadow-[0_1px_0_var(--border)]', arrowClassName),
    [arrowClassName]
  );

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <span
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled || undefined}
          aria-label={triggerAriaLabel}
          className="inline-block touch-manipulation relative"
        >
          {trigger}
        </span>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side={side}
          align={align}
          sideOffset={sideOffset}
          alignOffset={alignOffset}
          className={contentClassName}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onOpenAutoFocus={e => e.preventDefault()}
          onCloseAutoFocus={e => e.preventDefault()}
        >
          <div data-state="open">{children}</div>

          {showArrow && (
            <Popover.Arrow
              width={12}
              height={6}
              className={arrowClassNameMemo}
            />
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
});

export default HoverPopover;
